import {
    capitalize,
    Stack,
    Tooltip,
    Typography,
    Box,
    useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {CompetitiveGames, Maps, Team} from "../../../reckon-api";
import {useReferentialContext} from "../../../Context";
import {scrollable, getNeonColor} from "../../../theme";
import TeamIcon from "../../Icons/TeamIcon";
import AgentIcon from "../../Icons/AgentIcon";
import {SingleMapSelect} from "../MapSelect";
import {NeonCard, NeonCardContent, NeonCardHeader} from "../../../NeonComponents/NeonCards";
import {
    NeonTableHeader,
    NeonTableHeaderCell,
    NeonTableRow,
    NeonTableCell,
    NeonScoreBadge,
    NeonResultBadge,
    NeonEmptyState
} from "../../../NeonComponents/NeonTable";

// Column configuration - using flex for responsive layout
const COLUMNS = {
    date: { flex: 0.8, minWidth: '50px' },
    league: { flex: 1.8, minWidth: '100px' },
    map: { flex: 0.8, minWidth: '55px' },
    opponent: { flex: 1.5, minWidth: '100px' },
    composition: { flex: 2, minWidth: '140px' },
    score: { flex: 1.5, minWidth: '110px' },
    result: { flex: 0.4, minWidth: '30px' },
} as const;

export default function SpecificGameSelector(props: { 
    team: Team; 
    updateSelection: any; 
    paramObject: any; 
    single?: boolean; 
    sameMap?: boolean 
}) {
    const ctx = useReferentialContext();
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const default_map = ctx.mapsReferential.filter(m => m.id === 'lotus')[0]
    const [games, setGames] = React.useState([] as CompetitiveGames[]);
    const [selectedIndexes, setSelectedIndex] = React.useState([] as boolean[]);
    const [map, setMap] = useState(props.sameMap ? default_map : null as Maps | null);
    const api = useReferentialContext().reckonApi.competitiveGames;

    const handleChange = (clickedIndex: number) => {
        if(props.single){
            const nsi = games.map(() => false);
            nsi[clickedIndex] = true;
            props.updateSelection(games[clickedIndex]);
            setSelectedIndex(nsi);
        } else {
            const nsi = games.map((_, i) => selectedIndexes[i] ? true : false);
            nsi[clickedIndex] = !nsi[clickedIndex];
            props.updateSelection(games.filter((_, i) => nsi[i]));
            setSelectedIndex(nsi);
        }
    };

    useEffect(() => {
        setGames([]);
        const request_params = {...props.paramObject, ordering: ['-date','-id']}
        if(map){
            request_params.map = map.id;
        }
        api.list(request_params).then(r => setGames(r))
    }, [props.paramObject.watermark, map]);

    return (
        <NeonCard
            sx={{
                display: "flex",
                height: "100%",
                width: "100%",
                flexDirection: 'column',
            }}
        >
            <NeonCardHeader 
                action={<SingleMapSelect value={map} updateValue={setMap}/>}
                subheader={
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
                        {games.length} games
                    </Typography>
                }
                title={
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <TeamIcon team_name={props.team.name} sx={{ width: 28, height: 28 }}/>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                            Match History
                        </Typography>
                    </Stack>
                }
            />
            <NeonCardContent sx={{ 
                flex: 1,
                minHeight: 0,
                width: '100%', 
                padding: '8px 12px 16px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                {/* Header */}
                <NeonTableHeader>
                    <NeonTableHeaderCell flex={COLUMNS.date.flex} minWidth={COLUMNS.date.minWidth} align="left">Date</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.league.flex} minWidth={COLUMNS.league.minWidth} align="left">League</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.map.flex} minWidth={COLUMNS.map.minWidth} align="left">Map</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.opponent.flex} minWidth={COLUMNS.opponent.minWidth} align="left">Opponent</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.composition.flex} minWidth={COLUMNS.composition.minWidth} align="center">Comp</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.score.flex} minWidth={COLUMNS.score.minWidth} align="left">Score</NeonTableHeaderCell>
                    <NeonTableHeaderCell flex={COLUMNS.result.flex} minWidth={COLUMNS.result.minWidth} align="center"></NeonTableHeaderCell>
                </NeonTableHeader>

                {/* Games list */}
                <Box sx={{ 
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    ...scrollable,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    paddingRight: '4px',
                }}>
                        {games.map((game: CompetitiveGames, index: number) => {
                            const isWin = game.winner === props.team.id;
                            const isTeam1 = props.team.id === game.team1;
                            const teamAgents = isTeam1 ? game.team1_agents : game.team2_agents;
                            const teamScore = isTeam1 ? game.score_team1 : game.score_team2;
                            const opponentScore = isTeam1 ? game.score_team2 : game.score_team1;
                            const opponentId = isTeam1 ? game.team2 : game.team1;
                            const roundDiff = teamScore - opponentScore;
                            const opponentTeam = ctx.teamsReferential.find(t => t.id === opponentId);
                            const opponentName = opponentTeam?.name || opponentId;

                            // Check if we need a year divider
                            const gameYear = game.date ? dayjs(game.date).year() : null;
                            const prevGame = index > 0 ? games[index - 1] : null;
                            const prevYear = prevGame?.date ? dayjs(prevGame.date).year() : null;
                            const showYearDivider = index === 0 || (gameYear !== null && gameYear !== prevYear);

                            return (
                                <React.Fragment key={game.id || index}>
                                    {showYearDivider && gameYear && (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '10px 16px',
                                            marginTop: index === 0 ? 0 : '8px',
                                            marginBottom: '4px',
                                            borderRadius: '6px',
                                            background: `linear-gradient(90deg, ${neonColor}12 0%, ${neonColor}06 30%, rgba(255,255,255,0.02) 100%)`,
                                            borderLeft: `3px solid ${neonColor}`,
                                            boxShadow: `inset 0 0 20px ${neonColor}08`,
                                        }}>
                                            <Typography sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                color: 'rgba(255,255,255,0.9)',
                                                letterSpacing: '1px',
                                            }}>
                                                {gameYear} Season
                                            </Typography>
                                        </Box>
                                    )}
                                <Tooltip
                                    title={
                                        <Stack spacing={0.5} sx={{ padding: '4px' }}>
                                            <Typography variant="body2" fontWeight={600}>{game.league}</Typography>
                                            <Typography variant="caption">{game.split} • {game.phase}</Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                                {game.date ? dayjs(game.date).format("MMMM D, YYYY") : ''}
                                            </Typography>
                                        </Stack>
                                    }
                                    placement="left"
                                    arrow
                                >
                                    <Box>
                                        <NeonTableRow
                                            onClick={() => handleChange(index)}
                                            isSelected={selectedIndexes[index] === true}
                                            variant={isWin ? 'success' : 'error'}
                                            animationDelay={index}
                                        >
                                            {/* Date */}
                                            <NeonTableCell flex={COLUMNS.date.flex} minWidth={COLUMNS.date.minWidth} align="left">
                                                <Typography 
                                                    sx={{ 
                                                        fontSize: '0.65rem',
                                                        color: 'rgba(255,255,255,0.5)',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {game.date ? dayjs(game.date).format(
                                                        dayjs(game.date).year() === dayjs().year() ? "MMM DD" : "MMM 'YY"
                                                    ) : ''}
                                                </Typography>
                                            </NeonTableCell>

                                            {/* League/Event */}
                                            <NeonTableCell flex={COLUMNS.league.flex} minWidth={COLUMNS.league.minWidth} align="left">
                                                <Stack spacing={0} alignItems="flex-start">
                                                    <Typography 
                                                        sx={{ 
                                                            fontSize: '0.6rem',
                                                            fontWeight: 600,
                                                            color: 'rgba(255,255,255,0.85)',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: '100%',
                                                        }}
                                                    >
                                                        {game.league}
                                                    </Typography>
                                                    <Typography 
                                                        sx={{ 
                                                            fontSize: '0.5rem',
                                                            color: 'rgba(255,255,255,0.4)',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: '100%',
                                                        }}
                                                    >
                                                        {game.phase}{game.split ? ` • ${game.split}` : ''}
                                                    </Typography>
                                                </Stack>
                                            </NeonTableCell>

                                            {/* Map */}
                                            <NeonTableCell flex={COLUMNS.map.flex} minWidth={COLUMNS.map.minWidth} align="left">
                                                <Typography 
                                                    sx={{ 
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600,
                                                        color: 'rgba(255,255,255,0.85)',
                                                    }}
                                                >
                                                    {capitalize(game.map)}
                                                </Typography>
                                            </NeonTableCell>

                                            {/* Opponent */}
                                            <NeonTableCell flex={COLUMNS.opponent.flex} minWidth={COLUMNS.opponent.minWidth} align="left">
                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                    <TeamIcon 
                                                        team_name={opponentId} 
                                                        sx={{ width: 22, height: 22, flexShrink: 0 }}
                                                    />
                                                    <Typography sx={{ 
                                                        fontSize: '0.65rem',
                                                        fontWeight: 500,
                                                        color: 'rgba(255,255,255,0.7)',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}>
                                                        {opponentName}
                                                    </Typography>
                                                </Stack>
                                            </NeonTableCell>

                                            {/* Composition */}
                                            <NeonTableCell flex={COLUMNS.composition.flex} minWidth={COLUMNS.composition.minWidth} align="center">
                                                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                                    {teamAgents.split(',').map((agentName: string, i: number) => (
                                                        <AgentIcon 
                                                            key={i} 
                                                            agent_name={agentName} 
                                                            sx={{ width: 24, height: 24 }}
                                                        />
                                                    ))}
                                                </Stack>
                                            </NeonTableCell>

                                            {/* Score with round diff */}
                                            <NeonTableCell flex={COLUMNS.score.flex} minWidth={COLUMNS.score.minWidth} align="left">
                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <NeonScoreBadge 
                                                            score={teamScore} 
                                                            variant={isWin ? 'win' : 'loss'}
                                                            size="small"
                                                        />
                                                        <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.65rem' }}>-</Typography>
                                                        <NeonScoreBadge 
                                                            score={opponentScore} 
                                                            variant={!isWin ? 'win' : 'loss'}
                                                            size="small"
                                                        />
                                                    </Stack>
                                                    <Typography sx={{ 
                                                        fontSize: '0.6rem',
                                                        fontWeight: 700,
                                                        color: roundDiff > 0 ? '#2ecc71' : '#e74c3c',
                                                        minWidth: '28px',
                                                    }}>
                                                        {roundDiff > 0 ? `+${roundDiff}` : roundDiff}
                                                    </Typography>
                                                </Stack>
                                            </NeonTableCell>

                                            {/* Result */}
                                            <NeonTableCell flex={COLUMNS.result.flex} minWidth={COLUMNS.result.minWidth} align="center">
                                                <NeonResultBadge result={isWin ? 'W' : 'L'} />
                                            </NeonTableCell>
                                        </NeonTableRow>
                                    </Box>
                                </Tooltip>
                                </React.Fragment>
                            );
                        })}

                        {games.length === 0 && (
                            <NeonEmptyState message="No games found" />
                        )}
                    </Box>
            </NeonCardContent>
        </NeonCard>
    );
}
